import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller } from 'react-hook-form';
import { QFormControl, QMultiSelect, QSelect, QSelectPlaceholder, QText } from '@qualio/ui-components';
import { EventType } from '../../../types';
import ItemWithIdAndNameLabel from './ItemWithIdAndNameLabel';
import { CodeNameMultiSelect, CodeNameSelect } from './codeNameSelect';
import { FilterByEventType } from '../../../displayStrings';

export const EventTypeFilter = ({
  isLoadingEventTypes,
  eventTypes,
  isMultiSelect = false,
  isMultiSelectEventsListEnabled = false,
}: {
  isLoadingEventTypes: boolean;
  eventTypes: EventType[];
  isMultiSelect?: boolean;
  isMultiSelectEventsListEnabled?: boolean;
}) => {
  const { t } = useTranslation();

  const nativeEventTypesOptions = eventTypes.map((eventType) => ({
    value: eventType.lineage_id,
    label: eventType.name,
    code: eventType.prefix,
  }));

  const eventTypesOptions = eventTypes.map((eventType) => ({
    value: eventType.lineage_id,
    label: <ItemWithIdAndNameLabel itemName={eventType.name} itemId={eventType.prefix} setMinWidth={true} />,
  }));

  const multiSelectEventTypesOptions: Array<{ value: string; label: React.ReactNode }> = eventTypes.map(
    (eventType) => ({
      value: eventType.lineage_id,
      label: `${eventType.prefix} - ${eventType.name}`,
    }),
  );

  const eventTypeFilter = useCallback((option: any, searchText: string) => {
    const eventPrefix = option.data.label.props.itemId;
    const eventName = option.data.label.props.itemName;
    return !!(
      eventName.toLowerCase().includes(searchText.toLowerCase()) ||
      eventPrefix.toLowerCase().includes(searchText.toLowerCase())
    );
  }, []);

  return (
    <QFormControl
      label={isMultiSelect ? t('common.labels.eventTypes') : t('common.labels.eventType')}
      data-cy="event-type-filter"
      w="300px"
    >
      {isMultiSelect ? (
        isMultiSelectEventsListEnabled ? (
          <Controller
            name={'event_types'}
            render={({ field: { onChange, value } }) => (
              <QMultiSelect
                options={nativeEventTypesOptions}
                aria-label="event-type-filter"
                onChange={(selected) => onChange(selected.map((c) => c.value))}
                value={value ?? []}
                isLoading={isLoadingEventTypes}
              >
                <QSelectPlaceholder>
                  <QText>{FilterByEventType}</QText>
                </QSelectPlaceholder>
              </QMultiSelect>
            )}
          />
        ) : (
          <Controller
            name={'event_types'}
            render={({ field: { onChange, value } }) => (
              <CodeNameMultiSelect
                options={multiSelectEventTypesOptions}
                aria-label="event-type-filter"
                onChange={(val) => onChange(val.map((c) => c.value))}
                value={value ?? []}
                isLoading={isLoadingEventTypes}
              >
                <QSelectPlaceholder>
                  <QText>{FilterByEventType}</QText>
                </QSelectPlaceholder>
              </CodeNameMultiSelect>
            )}
          />
        )
      ) : isMultiSelectEventsListEnabled ? (
        <Controller
          name={'event_type'}
          render={({ field: { ref: _ref, onChange, value, ...field } }) => (
            <QSelect
              {...field}
              options={nativeEventTypesOptions}
              onChange={(changeObject) => {
                onChange(changeObject?.value ?? '');
              }}
              aria-label="event-type-filter"
              isClearable={true}
              value={value ?? ''}
              isLoading={isLoadingEventTypes}
              key={`event-type-${value}`}
            >
              <QSelectPlaceholder>
                <QText>{FilterByEventType}</QText>
              </QSelectPlaceholder>
            </QSelect>
          )}
        />
      ) : (
        <Controller
          name={'event_type'}
          render={({ field: { ref: _ref, onChange, value, ...field } }) => (
            <CodeNameSelect
              {...field}
              options={eventTypesOptions}
              onChange={(changeObject) => {
                onChange(changeObject?.value ?? '');
              }}
              aria-label="event-type-filter"
              isClearable={true}
              value={value ?? ''}
              isLoading={isLoadingEventTypes}
              filterOption={eventTypeFilter}
              key={`event-type-${value}`}
            >
              <QSelectPlaceholder>
                <QText>{FilterByEventType}</QText>
              </QSelectPlaceholder>
            </CodeNameSelect>
          )}
        />
      )}
    </QFormControl>
  );
};

export default EventTypeFilter;
